import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useNotification } from '../context/NotificationContext';
import AdminDataProvider from '../context/AdminDataContext';
import DomainUtils from '../utils/domainUtils';
import Header from './Header';
import SuspensionBanner from './dashboard/SuspensionBanner';
import ErrorBoundary from './ErrorBoundary';
// Import working admin components
import ModernDashboard from './admin/ModernDashboard';
import Users from './admin/Users';
import Teams from './admin/Teams';
import TeamInbox from './admin/TeamInbox';
import ModernRoleManagement from './admin/ModernRoleManagement';
import Domains from './admin/Domains';
import EmailMigration from './admin/email-migration';
import ModernStorage from './admin/ModernStorage';
import ModernReports from './admin/ModernReports';
import ModernSettings from './admin/ModernSettings';
import ModernSupport from './admin/ModernSupport';
import ModernBilling from './admin/ModernBilling';
import Invoices from './admin/Invoices';
import ERPDashboard from './admin/ERPDashboard';
import ERPInstances from './admin/ERPInstances';
import LicenseManagement from './admin/LicenseManagement';
import ERPModuleCatalog from './admin/ERPModuleCatalog';
import ERPUserManagement from './admin/ERPUserManagement';
import AICustomizer from './admin/AICustomizer';
import MailRules from './admin/MailRules';
import CloudflareOAuthCallback from './admin/domain/CloudflareOAuthCallback';
import AIDashboard from './admin/AI/AIDashboard';
import AIConfiguration from './admin/AI/AIConfiguration';
import AIRules from './admin/AI/AIRules';
import AIWorkflows from './admin/AI/AIWorkflows';
import AIAgents from './admin/AI/AIAgents';
import OnboardingWizard from './admin/Onboarding/OnboardingWizard';
import AIAgentConfig from './admin/AI/AIAgentConfig';
import AgentsContext from './admin/AI/AgentsContext';
import AIIntegrations from './admin/AI/AIIntegrations';
import AIKnowledge from './admin/AI/AIKnowledge';
import AITemplates from './admin/AI/AITemplates';
import AIAnalytics from './admin/AI/AIAnalytics';
import AITesting from './admin/AI/AITesting';
import ProductDemos from './admin/AI/ProductDemos';
// TwilioManagement deprecated - redirects to DIDManagement (Voice > DIDs)
import AIRecruitment from './admin/AI/AIRecruitment';
import WordPressManagement from './admin/AI/WordPressManagement';
import {
  VoiceDashboard,
  TrunkManagement,
  DIDManagement,
  CallHistory,
  RateCards,
  ActiveCalls,
  VoiceSettings
} from './admin/Voice';
import ChatWidget from './ui/ChatWidget';
import TrialExpirationModal from './dashboard/TrialExpirationModal';
// Heroicons imports
import {
  HomeIcon, UserGroupIcon, ShieldCheckIcon, GlobeAltIcon,
  EnvelopeIcon, CloudIcon, Cog6ToothIcon, QuestionMarkCircleIcon, FunnelIcon,
  BuildingOfficeIcon, BellIcon, UserIcon, ArrowRightOnRectangleIcon,
  ChartBarIcon, XMarkIcon, RectangleStackIcon, CheckCircleIcon, ExclamationCircleIcon,
  CreditCardIcon, CurrencyDollarIcon, ShoppingCartIcon, TagIcon, ServerIcon, SparklesIcon,
  ChevronDownIcon, ChevronRightIcon, BoltIcon, Cog8ToothIcon, PuzzlePieceIcon,
  AcademicCapIcon, BeakerIcon, CpuChipIcon, DocumentTextIcon, MegaphoneIcon,
  PhoneIcon, PhoneArrowUpRightIcon, ClockIcon, PresentationChartBarIcon
} from '@heroicons/react/24/outline';
import { PanelLeftClose, PanelLeftOpen, Menu as MenuIcon } from 'lucide-react';
import {
  HomeIcon as HomeIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  ShieldCheckIcon as ShieldCheckIconSolid,
  GlobeAltIcon as GlobeAltIconSolid,
  EnvelopeIcon as EnvelopeIconSolid,
  CloudIcon as CloudIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
  QuestionMarkCircleIcon as QuestionMarkCircleIconSolid,
  BuildingOfficeIcon as BuildingOfficeIconSolid,
  ChartBarIcon as ChartBarIconSolid,
  RectangleStackIcon as RectangleStackIconSolid,
  CreditCardIcon as CreditCardIconSolid,
  CurrencyDollarIcon as CurrencyDollarIconSolid,
  ShoppingCartIcon as ShoppingCartIconSolid,
  TagIcon as TagIconSolid,
  ServerIcon as ServerIconSolid,
  SparklesIcon as SparklesIconSolid,
  FunnelIcon as FunnelIconSolid,
  BoltIcon as BoltIconSolid,
  Cog8ToothIcon as Cog8ToothIconSolid,
  PuzzlePieceIcon as PuzzlePieceIconSolid,
  AcademicCapIcon as AcademicCapIconSolid,
  BeakerIcon as BeakerIconSolid,
  CpuChipIcon as CpuChipIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  MegaphoneIcon as MegaphoneIconSolid,
  PhoneIcon as PhoneIconSolid,
  PhoneArrowUpRightIcon as PhoneArrowUpRightIconSolid,
  ClockIcon as ClockIconSolid,
  PresentationChartBarIcon as PresentationChartBarIconSolid
} from '@heroicons/react/24/solid';
// Moved CheckCircleIcon and ExclamationCircleIcon to the main import

const allMenuItems = [
  {
    category: 'Overview',
    items: [
      { title: 'Dashboard', icon: HomeIcon, solidIcon: HomeIconSolid, path: 'dashboard', permissions: [] }, // Always visible
    ],
  },
  {
    category: 'User Management',
    items: [
      { title: 'Users', icon: UserGroupIcon, solidIcon: UserGroupIconSolid, path: 'users', permissions: ['users:read'] },
      { title: 'Teams', icon: BuildingOfficeIcon, solidIcon: BuildingOfficeIconSolid, path: 'teams', permissions: ['users:read'] },
      { title: 'Roles & Permissions', icon: ShieldCheckIcon, solidIcon: ShieldCheckIconSolid, path: 'roles', permissions: ['security:manage_roles'] },
    ],
  },
  {
    category: 'Domain Management',
    items: [
      { title: 'Domains', icon: GlobeAltIcon, solidIcon: GlobeAltIconSolid, path: 'domains', permissions: ['domains:read'] },
      { title: 'Mail Rules', icon: FunnelIcon, solidIcon: FunnelIconSolid, path: 'mail-rules', permissions: ['emails:read'] },
      { title: 'Email Migration', icon: EnvelopeIcon, solidIcon: EnvelopeIconSolid, path: 'email-settings', permissions: ['emails:read'] },
      { title: 'Storage', icon: CloudIcon, solidIcon: CloudIconSolid, path: 'storage', permissions: ['analytics:view_basic'] },
    ],
  },
  {
    category: 'Business Apps',
    items: [
      {
        title: 'Tellency',
        icon: RectangleStackIcon,
        solidIcon: RectangleStackIconSolid,
        isExpandable: true,
        permissions: ['users:read'],
        subItems: [
          { title: 'Dashboard', icon: RectangleStackIcon, solidIcon: RectangleStackIconSolid, path: 'erp-dashboard', permissions: ['users:read'] },
          { title: 'Instances', icon: ServerIcon, solidIcon: ServerIconSolid, path: 'erp-instances', permissions: ['users:read'] },
          // { title: 'AI Customizer', icon: SparklesIcon, solidIcon: SparklesIconSolid, path: 'ai-customizer', permissions: ['users:read'], badge: 'NEW' },
          { title: 'Module Catalog', icon: ShoppingCartIcon, solidIcon: ShoppingCartIconSolid, path: 'erp-catalog', permissions: [] },
          { title: 'User Access', icon: UserGroupIcon, solidIcon: UserGroupIconSolid, path: 'erp-users', permissions: ['users:read'] },
        ]
      },
    ],
  },
  {
    category: 'AI & Automation',
    items: [
      {
        title: 'AI',
        icon: SparklesIcon,
        solidIcon: SparklesIconSolid,
        isExpandable: true,
        permissions: [],
        subItems: [
          { title: 'Dashboard', icon: SparklesIcon, solidIcon: SparklesIconSolid, path: 'ai-dashboard', permissions: [] },
          { title: 'Configuration', icon: Cog8ToothIcon, solidIcon: Cog8ToothIconSolid, path: 'ai-configuration', permissions: [] },
          { title: 'Rules', icon: BoltIcon, solidIcon: BoltIconSolid, path: 'ai-rules', permissions: [] },
          { title: 'Workflows', icon: RectangleStackIcon, solidIcon: RectangleStackIconSolid, path: 'ai-workflows', permissions: [] },
          { title: 'Agents', icon: CpuChipIcon, solidIcon: CpuChipIconSolid, path: 'ai-agents', permissions: [] },
          // { title: 'Agents Context', icon: ServerIcon, solidIcon: ServerIconSolid, path: 'ai-agents-context', permissions: [] },
          // { title: 'Recruitment', icon: UserGroupIcon, solidIcon: UserGroupIconSolid, path: 'ai-recruitment', permissions: [], badge: 'NEW' },
          // { title: 'Integrations', icon: PuzzlePieceIcon, solidIcon: PuzzlePieceIconSolid, path: 'ai-integrations', permissions: [] },
          { title: 'Knowledge Base', icon: AcademicCapIcon, solidIcon: AcademicCapIconSolid, path: 'ai-knowledge', permissions: [] },
          { title: 'Templates', icon: DocumentTextIcon, solidIcon: DocumentTextIconSolid, path: 'ai-templates', permissions: [] },
          // { title: 'Analytics', icon: ChartBarIcon, solidIcon: ChartBarIconSolid, path: 'ai-analytics', permissions: [] },
          { title: 'Testing', icon: BeakerIcon, solidIcon: BeakerIconSolid, path: 'ai-testing', permissions: [] },
          { title: 'Product Demos', icon: PresentationChartBarIcon, solidIcon: PresentationChartBarIconSolid, path: 'ai-product-demos', permissions: [] },
        ]
      },
    ],
  },
  {
    category: 'Voice',
    items: [
      {
        title: 'Voice',
        icon: PhoneIcon,
        solidIcon: PhoneIconSolid,
        isExpandable: true,
        permissions: ['voice:read'],
        subItems: [
          { title: 'Dashboard', icon: PhoneIcon, solidIcon: PhoneIconSolid, path: 'voice', permissions: ['voice:read'] },
          { title: 'SIP Trunks', icon: ServerIcon, solidIcon: ServerIconSolid, path: 'voice/trunks', permissions: ['voice:manage'] },
          { title: 'Phone Numbers', icon: PhoneIcon, solidIcon: PhoneIconSolid, path: 'voice/dids', permissions: ['voice:manage'] },
          { title: 'Call History', icon: ClockIcon, solidIcon: ClockIconSolid, path: 'voice/history', permissions: ['voice:read'] },
          { title: 'Rate Cards', icon: CurrencyDollarIcon, solidIcon: CurrencyDollarIconSolid, path: 'voice/ratecards', permissions: ['voice:billing'] },
          { title: 'Active Calls', icon: PhoneArrowUpRightIcon, solidIcon: PhoneArrowUpRightIconSolid, path: 'voice/active', permissions: ['voice:read'] },
          { title: 'Settings', icon: Cog8ToothIcon, solidIcon: Cog8ToothIconSolid, path: 'voice/settings', permissions: ['voice:manage'] },
        ]
      },
    ],
  },
  {
    category: 'Analytics',
    items: [
      { title: 'Reports', icon: ChartBarIcon, solidIcon: ChartBarIconSolid, path: 'reports', permissions: ['analytics:view_basic'] },
    ],
  },
  {
    category: 'Billing & Account',
    items: [
      { title: 'Billing', icon: CreditCardIcon, solidIcon: CreditCardIconSolid, path: 'billing', permissions: [] },
      { title: 'License Overview', icon: CurrencyDollarIcon, solidIcon: CurrencyDollarIconSolid, path: 'license-management', permissions: [] },
    ],
  },
  {
    category: 'Configuration',
    items: [
      { title: 'Settings', icon: Cog6ToothIcon, solidIcon: Cog6ToothIconSolid, path: 'settings', permissions: ['settings:read'] },
      { title: 'Support', icon: QuestionMarkCircleIcon, solidIcon: QuestionMarkCircleIconSolid, path: 'support', permissions: [] }, // Always visible
    ],
  },
];

// Simple Dashboard Content Component
const DashboardContent = ({ showNotification }) => {
  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
        <p className="text-gray-600">Welcome to the AiMail Admin Dashboard</p>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="dashboard-card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserGroupIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">System Status</dt>
                <dd className="text-lg font-medium text-green-600">Active</dd>
              </dl>
            </div>
          </div>
        </div>
        
        <div className="dashboard-card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ShieldCheckIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Admin Access</dt>
                <dd className="text-lg font-medium text-green-600">Verified</dd>
              </dl>
            </div>
          </div>
        </div>
        
        <div className="dashboard-card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Cog6ToothIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Configuration</dt>
                <dd className="text-lg font-medium text-gray-900">Ready</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
      
      {/* Welcome Message */}
      <div className="dashboard-card">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Welcome to Admin Portal</h2>
        <div className="text-gray-600">
          <p className="mb-2">You have successfully accessed the Admin Dashboard.</p>
          <p className="text-sm">Use the sidebar to navigate between different admin functions.</p>
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-blue-800 text-sm">
              <strong>Note:</strong> This admin interface provides access to user management and system settings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder components for other sections
const PlaceholderComponent = ({ title, showNotification }) => (
  <div className="p-6">
    <h1 className="text-3xl font-bold text-gray-900 mb-4">{title}</h1>
    <div className="dashboard-card">
      <p className="text-gray-500 text-center py-8">
        {title} functionality will be implemented here.
      </p>
      <div className="mt-4 text-center">
        <button 
          onClick={() => showNotification && showNotification(`${title} feature coming soon!`, 'info')}
          className="btn-admin-secondary"
        >
          Test Notification
        </button>
      </div>
    </div>
  </div>
);

const BILLING_LOCK_STATUSES = ['expired', 'suspended', 'deactivated'];
const ADMIN_LOCK_STATUSES = ['suspended', 'deactivated'];

const AdminDashboard = ({ hideHeader = false }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [expandedItems, setExpandedItems] = useState([]); // Default collapsed
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [showTrialModal, setShowTrialModal] = useState(true);
  const sidebarScrollRef = React.useRef(null);

  // Get current selected item from URL path
  const selectedItem = useMemo(() => {
    const path = location.pathname.split('/').pop();
    return path || 'dashboard';
  }, [location.pathname]);

  // Restore scroll position only when component mounts or route changes
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('adminSidebarScrollPosition');
    if (sidebarScrollRef.current && savedScrollPosition) {
      const scrollPos = parseInt(savedScrollPosition, 10);
      if (scrollPos > 0) {
        // Restore immediately without delay
        sidebarScrollRef.current.scrollTop = scrollPos;
      }
    }
  }, [location.pathname]); // Only run when route changes
  
  // Get user data and permissions with memoization
  const { userData, loading: userLoading, isInitialized } = useUser();
  
  // Subscription enforcement: derive lock state from userData
  const subscriptionStatus = userData?.business?.subscriptionDetails?.status;
  const isBillingLocked = BILLING_LOCK_STATUSES.includes(subscriptionStatus);
  const isOwnerOrAdmin = userData?.isBusinessOwner || userData?.isAdmin ||
    userData?.roleInfo?.level >= 60;
  // Non-owner users are fully locked out when account is suspended/deactivated
  const isUserLockedOut = ADMIN_LOCK_STATUSES.includes(subscriptionStatus) && !isOwnerOrAdmin;

  // Redirect admin/owner to /billing when status requires it (except if already there)
  useEffect(() => {
    if (isBillingLocked && isOwnerOrAdmin && location.pathname !== '/billing') {
      navigate('/billing', { replace: true });
    }
  }, [isBillingLocked, isOwnerOrAdmin, location.pathname, navigate]);

  // Memoize user access level to prevent excessive permission checks
  const userAccessLevel = useMemo(() => {
    if (!userData) return 'none';
    
    // CRITICAL: Business owners should ALWAYS have full access
    if (userData.isBusinessOwner === true) {
      return 'business_owner';
    }
    
    // Check if user is super_admin or has high-level access (level >= 80)
    if (userData.roleInfo?.name === 'super_admin' || userData.roleInfo?.level >= 80) {
      return 'super_admin';
    }
    
    // Check legacy isAdmin flag
    if (userData.isAdmin === true) {
      return 'admin';
    }
    
    return 'standard';
  }, [userData?.isBusinessOwner, userData?.isAdmin, userData?.roleInfo?.name, userData?.roleInfo?.level]);

  // Memoize permission checking to prevent unnecessary re-calculations
  const hasPermission = useCallback((requiredPermissions) => {
    if (!requiredPermissions || requiredPermissions.length === 0) return true; // Always visible items
    
    // Fast path for elevated access levels
    if (userAccessLevel === 'business_owner' || userAccessLevel === 'super_admin' || userAccessLevel === 'admin') {
      return true;
    }
    
    if (!userData?.roleInfo?.permissions) {
      return false;
    }
    
    const userPermissions = userData.roleInfo.permissions;
    // User needs at least one of the required permissions
    return requiredPermissions.some(permission => userPermissions.includes(permission));
  }, [userAccessLevel, userData?.roleInfo?.permissions]);
  
  // Filter menu items based on user permissions
  const menuItems = useMemo(() => {
    // If no userData yet, return all items to prevent blank sidebar
    if (!userData) {
      return allMenuItems;
    }
    
    const filteredItems = allMenuItems.map(category => {
      const filteredCategoryItems = category.items.filter(item => {
        return hasPermission(item.permissions);
      });
      
      return {
        ...category,
        items: filteredCategoryItems
      };
    }).filter(category => category.items.length > 0); // Remove empty categories
    
    return filteredItems;
  }, [userData?.roleInfo?.permissions, userData, hasPermission, userAccessLevel]);

  // Handle navigation with scroll position preservation
  const handleNavigate = useCallback((path) => {
    // Save scroll position before navigation
    if (sidebarScrollRef.current) {
      sessionStorage.setItem('adminSidebarScrollPosition', sidebarScrollRef.current.scrollTop.toString());
    }
    navigate(path);
    if (mobileOpen) setMobileOpen(false);
  }, [navigate, mobileOpen]);

  const notify = useNotification();
  // Bridge for child components that receive showNotification as a prop
  const showNotification = useCallback((message, type = 'error') => {
    notify(message, type);
  }, [notify]);

  // Wait for UserContext to initialize before rendering
  if (!isInitialized || userLoading) {
    return (
      <div className="flex flex-col h-screen bg-gray-50 dark:bg-black">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 bg-orange-600 rounded-lg animate-pulse"></div>
        </div>
      </div>
    );
  }
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleNotificationClick = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleSearch = (query) => {
    setSearchTerm(query.toLowerCase());
  };
  
  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };
  
  const handleLogout = () => {
    handleMenuClose();
  };

  const toggleExpanded = (itemTitle) => {
    const currentScroll = sidebarScrollRef.current?.scrollTop || 0;

    setExpandedItems(prev =>
      prev.includes(itemTitle)
        ? prev.filter(t => t !== itemTitle)
        : [...prev, itemTitle]
    );

    // Wait for DOM update, then restore scroll position
    requestAnimationFrame(() => {
      if (sidebarScrollRef.current) {
        sidebarScrollRef.current.scrollTop = currentScroll;
        sessionStorage.setItem('adminSidebarScrollPosition', currentScroll.toString());
      }
    });
  };

  const NavigationItem = ({ item, onNavigate }) => {
    const Icon = item.icon;
    const SolidIcon = item.solidIcon;
    const isActive = selectedItem === item.path;
    const isExpanded = expandedItems.includes(item.title);
    const isExpandable = item.isExpandable && item.subItems;

    // Check if any sub-item is active
    const hasActiveSubItem = isExpandable && item.subItems?.some(subItem => selectedItem === subItem.path);

    if (isExpandable) {
      return (
        <li>
          {/* Parent expandable item */}
          <button
            onClick={() => toggleExpanded(item.title)}
            className={`w-full flex items-center ${sidebarCollapsed ? 'px-2 py-2.5 justify-center' : 'px-3 py-2.5'} rounded-lg transition-colors duration-200 group relative ${
              hasActiveSubItem
                ? 'bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white'
            }`}
            title={sidebarCollapsed ? item.title : ''}
          >
            {hasActiveSubItem && !sidebarCollapsed && (
              <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r-full bg-orange-500" aria-hidden="true" />
            )}
            <Icon className={`w-5 h-5 ${hasActiveSubItem ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white'}`} />
            {!sidebarCollapsed && (
              <>
                <span className={`ml-3 text-sm font-medium flex-1 text-left flex items-center gap-2 ${hasActiveSubItem ? 'text-orange-600 dark:text-orange-400' : 'text-gray-600 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white'}`}>
                  {item.title}
                  {item.badge && (
                    <span className="px-1.5 py-0.5 text-xs font-semibold bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 rounded">
                      {item.badge}
                    </span>
                  )}
                </span>
                {isExpanded ? (
                  <ChevronDownIcon className={`w-3 h-3 ${hasActiveSubItem ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400 dark:text-gray-500'}`} />
                ) : (
                  <ChevronRightIcon className={`w-3 h-3 ${hasActiveSubItem ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400 dark:text-gray-500'}`} />
                )}
              </>
            )}
          </button>

          {/* Sub-items */}
          {!sidebarCollapsed && isExpanded && (
            <ul className="mt-1 space-y-1 ml-4 pl-3 border-l border-gray-200 dark:border-gray-800">
              {item.subItems.map((subItem) => {
                const SubIcon = subItem.icon;
                const SubSolidIcon = subItem.solidIcon;
                const isSubActive = selectedItem === subItem.path;

                return (
                  <li key={subItem.path || subItem.title}>
                    <button
                      onClick={() => onNavigate(subItem.path)}
                      className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors duration-200 group ${
                        isSubActive
                          ? 'bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      {isSubActive ? (
                        <SubSolidIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                      ) : (
                        <SubIcon className="w-4 h-4 text-gray-500 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-400" />
                      )}
                      <span className={`ml-2 text-sm font-medium flex items-center gap-2 ${isSubActive ? 'text-orange-600 dark:text-orange-400' : 'text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white'}`}>
                        {subItem.title}
                        {subItem.badge && (
                          <span className="px-1.5 py-0.5 text-xs font-semibold bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 rounded">
                            {subItem.badge}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </li>
      );
    }

    // Regular non-expandable item
    return (
      <li>
        <button
          onClick={() => onNavigate(item.path)}
          className={`w-full flex items-center ${sidebarCollapsed ? 'px-2 py-2.5 justify-center' : 'px-3 py-2.5'} rounded-lg transition-colors duration-200 group relative ${
            isActive
              ? 'bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white'
          }`}
          title={sidebarCollapsed ? item.title : ''}
        >
          {/* Left accent bar — a rounded pill that reads cleanly against the
              rounded button (the old straight border-l fought the corners). */}
          {isActive && !sidebarCollapsed && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r-full bg-orange-500" aria-hidden="true" />
          )}
          {isActive ? (
            <SolidIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
          ) : (
            <Icon className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white" />
          )}
          {!sidebarCollapsed && (
            <span className={`ml-3 text-sm font-medium truncate flex items-center gap-2 ${isActive ? 'text-orange-600 dark:text-orange-400' : 'text-gray-600 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white'}`}>
              {item.title}
              {item.badge && (
                <span className="px-1.5 py-0.5 text-xs font-semibold bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 rounded">
                  {item.badge}
                </span>
              )}
            </span>
          )}
          {sidebarCollapsed && (
            <div className="absolute left-full ml-3 px-3 py-2 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-50 whitespace-nowrap pointer-events-none shadow-lg">
              {item.title}
            </div>
          )}
        </button>
      </li>
    );
  };

  const Navigation = () => (
    <div className="h-full flex flex-col">
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Cog6ToothIconSolid className="w-4 h-4 text-primary-foreground" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Admin</h1>
        </div>
        <button
          onClick={handleDrawerToggle}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation Content */}
      <div
        ref={sidebarScrollRef}
        className="flex-1 overflow-y-auto py-4"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          scrollBehavior: 'auto',
          overflowAnchor: 'none'
        }}
        onScroll={(e) => {
          // Save scroll position on every scroll
          sessionStorage.setItem('adminSidebarScrollPosition', e.target.scrollTop.toString());
        }}
      >
        <div className={`space-y-6 ${sidebarCollapsed ? 'px-2' : 'px-4'}`}>
          {menuItems.map((category, index) => (
            <div key={category.category}>
              {!sidebarCollapsed && (
                <div className="flex items-center justify-between px-3 mb-3">
                  <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {category.category}
                  </h2>
                  {index === 0 && (
                    <button
                      onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                      className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                      title="Collapse sidebar"
                    >
                      <PanelLeftClose className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
              {sidebarCollapsed && index === 0 && (
                <div className="flex justify-center mb-3">
                  <button
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                    title="Expand sidebar"
                  >
                    <PanelLeftOpen className="w-5 h-5" />
                  </button>
                </div>
              )}
              <ul className="space-y-1">
                {category.items.map((item) => (
                  <NavigationItem key={item.title} item={item} onNavigate={handleNavigate} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Trial indicator */}
      {userData?.business?.subscriptionDetails?.status === 'trial' && !sidebarCollapsed && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Trial &middot; {(() => {
                const endDate = new Date(userData.business.subscriptionDetails.trialEndDate);
                const days = Math.max(0, Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24)));
                return `${days}d left`;
              })()}
            </span>
            <button
              onClick={() => handleNavigate('billing')}
              className="text-xs font-medium text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300"
            >
              Upgrade
            </button>
          </div>
        </div>
      )}

    </div>
  );

  // Non-admin users fully locked out when account is suspended/deactivated
  if (isUserLockedOut) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center p-6">
        <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 max-w-md w-full p-10 text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <ExclamationCircleIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Account Suspended</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-8">
            Your organisation's subscription has expired. Please contact your account administrator to restore access.
          </p>
          <a
            href="mailto:support@aiinak.com"
            className="inline-block px-6 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Contact Support
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-black m-0 p-0">
      {!hideHeader && <Header />}
      <SuspensionBanner />

      {/* Mobile Header */}
      <div className="lg:hidden bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={handleDrawerToggle}
            className="p-2 rounded-lg text-gray-600 dark:text-white hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <MenuIcon className="w-5 h-5" />
          </button>
          
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-primary rounded-md flex items-center justify-center">
              <Cog6ToothIconSolid className="w-3 h-3 text-primary-foreground" />
            </div>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">Admin</h1>
          </div>
          <div className="w-9"></div> {/* Spacer for centering */}
        </div>
      </div>
      
      <div className="flex flex-1 overflow-hidden m-0 p-0">
        {/* Mobile Navigation Overlay */}
        <div 
          className={`fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300 ${
            mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`} 
          onClick={handleDrawerToggle}
        />
        
        {/* Sidebar */}
        <aside className={`fixed top-0 left-0 ${sidebarCollapsed ? 'w-16' : 'w-60'} h-full bg-white dark:bg-[#1a1a1a] border-r border-gray-200 dark:border-gray-800 shadow-sm z-50 transform transition-all duration-300 lg:translate-x-0 lg:static lg:z-auto ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:w-${sidebarCollapsed ? '16' : '60'}`}>
          <Navigation />
        </aside>
        
        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <div className="h-full">
            <AdminDataProvider>
              <Routes>
                <Route path="dashboard" element={<ModernDashboard showNotification={showNotification} />} />
                <Route path="users" element={<Users showNotification={showNotification} />} />
                <Route path="teams" element={<Teams showNotification={showNotification} />} />
                <Route path="teams/:teamId/inbox" element={<TeamInbox showNotification={showNotification} />} />
                <Route path="roles" element={<ModernRoleManagement showNotification={showNotification} />} />
                <Route path="domains" element={<Domains showNotification={showNotification} />} />
                <Route path="oauth/cloudflare/callback" element={<CloudflareOAuthCallback />} />
                <Route path="mail-rules" element={<MailRules showNotification={showNotification} />} />
                <Route path="email-settings" element={<ErrorBoundary><EmailMigration showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="storage" element={<ModernStorage showNotification={showNotification} />} />
                <Route path="erp-dashboard" element={<ErrorBoundary><ERPDashboard showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="erp-instances" element={<ErrorBoundary><ERPInstances showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="erp-catalog" element={<ErrorBoundary><ERPModuleCatalog showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="erp-users" element={<ErrorBoundary><ERPUserManagement showNotification={showNotification} /></ErrorBoundary>} />
                {/* <Route path="ai-customizer" element={<ErrorBoundary><AICustomizer showNotification={showNotification} /></ErrorBoundary>} /> */}
                <Route path="ai-dashboard" element={<ErrorBoundary><AIDashboard showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="ai-configuration" element={<ErrorBoundary><AIConfiguration showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="ai-rules" element={<ErrorBoundary><AIRules showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="ai-workflows" element={<ErrorBoundary><AIWorkflows showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="ai-agents" element={<ErrorBoundary><AIAgents showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="onboarding" element={<ErrorBoundary><OnboardingWizard showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="ai-agent-config/:agentId" element={<ErrorBoundary><AIAgentConfig showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="ai-agents-context" element={<ErrorBoundary><AgentsContext showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="ai-recruitment" element={<ErrorBoundary><AIRecruitment showNotification={showNotification} /></ErrorBoundary>} />
                {/* <Route path="ai-integrations" element={<AIIntegrations showNotification={showNotification} />} /> */}
                <Route path="twilio-management" element={<Navigate to="/voice/dids" replace />} />
                <Route path="wordpress-management" element={<ErrorBoundary><WordPressManagement showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="ai-knowledge" element={<ErrorBoundary><AIKnowledge showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="ai-templates" element={<ErrorBoundary><AITemplates showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="ai-analytics" element={<ErrorBoundary><AIAnalytics showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="ai-testing" element={<ErrorBoundary><AITesting showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="ai-product-demos" element={<ErrorBoundary><ProductDemos showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="voice" element={<ErrorBoundary><VoiceDashboard showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="voice/trunks" element={<ErrorBoundary><TrunkManagement showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="voice/dids" element={<ErrorBoundary><DIDManagement showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="voice/history" element={<ErrorBoundary><CallHistory showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="voice/ratecards" element={<ErrorBoundary><RateCards showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="voice/active" element={<ErrorBoundary><ActiveCalls showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="voice/settings" element={<ErrorBoundary><VoiceSettings showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="reports" element={<ModernReports showNotification={showNotification} />} />
                <Route path="billing" element={<ErrorBoundary><ModernBilling showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="invoices" element={<ErrorBoundary><Invoices showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="license-management" element={<ErrorBoundary><LicenseManagement showNotification={showNotification} /></ErrorBoundary>} />
                <Route path="settings" element={<ModernSettings showNotification={showNotification} />} />
                <Route path="support" element={<ModernSupport showNotification={showNotification} />} />
                <Route path="" element={<Navigate to="dashboard" replace />} />
              </Routes>
            </AdminDataProvider>
          </div>
        </main>
      </div>
      

      {/* Trial Expiration Modal */}
      {showTrialModal && userData?.business?.subscriptionDetails && (
        <TrialExpirationModal
          subscriptionDetails={userData.business.subscriptionDetails}
          onClose={() => setShowTrialModal(false)}
        />
      )}

      {/* Chat Widget hidden */}
      {/* <ChatWidget showNotification={showNotification} /> */}
    </div>
  );
};

export default AdminDashboard;